import { useState } from 'react'
import { Alert, Box, Card, CardContent, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import { wfAutoPropose, serverMessage } from './api'
import { fmtKg, fmtDate } from './format'

const cellSx = { fontSize: 12, py: 0.75, px: 1 }
const headSx = { ...cellSx, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#888', fontWeight: 700 }
const OUTCOME_LABEL = {
  PROPOSED: 'Proposed', DRY_RUN: 'Would propose', ALREADY_ON_TABLE: 'Already has a request', DECLINED_IN_WINDOW: 'Declined recently',
}

/**
 * T12/T13 — the auto-proposal engine, run now. A dry run lists what the rule would propose and
 * writes nothing; a live run creates PROPOSED requests the merchant then accepts or declines.
 * The scheduler runs the same code; this exists for the first months, when the rule is expected
 * to fire rarely (T12 + T14 together) and ops will want to see why.
 */
export default function AutoProposalCard({ merchant }) {
  const { enqueueSnackbar } = useSnackbar()
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState('')

  const run = async (dryRun, scoped) => {
    setBusy(`${dryRun ? 'dry' : 'live'}-${scoped ? 'one' : 'all'}`)
    try {
      const r = await wfAutoPropose(dryRun, scoped && merchant ? merchant.value : undefined)
      setResult(r)
      enqueueSnackbar(dryRun
        ? `Dry run: ${r.qualifying} SKU${r.qualifying === 1 ? '' : 's'} qualify, ${r.skippedAlreadyOnTable} already have a request`
        : `${r.proposed} proposal${r.proposed === 1 ? '' : 's'} created`, { variant: 'success' })
    } catch (err) {
      enqueueSnackbar(serverMessage(err, 'The engine run failed'), { variant: 'error' })
    } finally {
      setBusy('')
    }
  }

  return (
    <Card sx={{ maxWidth: 960, mt: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
      <CardContent>
        <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
          <AutoAwesomeOutlinedIcon sx={{ fontSize: 18, color: '#5e35b1' }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Auto-proposed weight freezes</Typography>
        </Stack>
        <Typography sx={{ fontSize: 12, color: '#4b5563', mb: 1.5 }}>
          When a merchant has accepted the courier's weight on the same SKU enough times inside the window (business default: 2 in 2 months,
          counted from go-live and merchant-accepted only), Prozo proposes a Merchant Weight Freeze at the median of those weights.
          The merchant accepts or declines it in their panel; nothing bills until they accept and the new weight rule is switched on for them.
        </Typography>
        <Stack direction='row' spacing={1} flexWrap='wrap' rowGap={1}>
          <LoadingButton variant='outlined' size='small' loading={busy === 'dry-all'} disabled={!!busy} onClick={() => run(true, false)} sx={{ fontSize: 12 }}>
            Dry run — all merchants
          </LoadingButton>
          <LoadingButton variant='outlined' size='small' loading={busy === 'dry-one'} disabled={!!busy || !merchant} onClick={() => run(true, true)} sx={{ fontSize: 12 }}>
            Dry run — this merchant
          </LoadingButton>
          <LoadingButton variant='contained' size='small' color='secondary' loading={busy === 'live-one'} disabled={!!busy || !merchant} onClick={() => run(false, true)} sx={{ fontSize: 12, fontWeight: 700 }}>
            Create proposals for this merchant
          </LoadingButton>
        </Stack>

        {result && (
          <Box sx={{ mt: 2, display: 'grid', gap: 1.5 }}>
            <Alert severity={result.note ? 'warning' : 'info'} sx={{ fontSize: 12 }}>
              {result.note
                ? result.note
                : `${result.dryRun ? 'Dry run' : 'Live run'} · rule ${result.minAccepts} accepts in ${result.windowMonths} months · window ${fmtDate(result.windowFrom)} → ${fmtDate(result.windowTo)} · ${result.acceptedRowsScanned} accepted discrepancies over ${result.skuGroups} SKUs · ${result.qualifying} qualify · ${result.proposed} proposed · ${result.skippedAlreadyOnTable} already on the table · ${result.skippedDeclinedInWindow} declined recently`}
            </Alert>
            {result.candidates && result.candidates.length > 0 && (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size='small' sx={{ minWidth: 640 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>Merchant</TableCell>
                      <TableCell sx={headSx}>SKU</TableCell>
                      <TableCell sx={headSx} align='right'>Accepted</TableCell>
                      <TableCell sx={headSx} align='right'>Median</TableCell>
                      <TableCell sx={headSx}>Weights accepted</TableCell>
                      <TableCell sx={headSx}>Outcome</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.candidates.map((c) => (
                      <TableRow key={`${c.merchantId}-${c.sku}`}>
                        <TableCell sx={cellSx}>{c.merchantName || c.merchantId}</TableCell>
                        <TableCell sx={cellSx}>{c.sku}{c.productName ? <Typography sx={{ fontSize: 11, color: '#6b7280' }}>{c.productName}</Typography> : null}</TableCell>
                        <TableCell sx={cellSx} align='right'>{c.accepted}</TableCell>
                        <TableCell sx={{ ...cellSx, fontWeight: 700 }} align='right'>{fmtKg(c.medianGm / 1000)}</TableCell>
                        <TableCell sx={cellSx}>{(c.weightsGm || []).map((g) => (g / 1000).toFixed(2)).join(', ')} kg</TableCell>
                        <TableCell sx={cellSx}>{OUTCOME_LABEL[c.outcome] || c.outcome}{c.requestRef ? ` · ${c.requestRef}` : ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
